import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DownloadOutlined,
  EditOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PlusOutlined,
  PrinterOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tooltip,
} from 'antd';
import {
  fetchDieToolByDieProfileId,
  resetCurrentToolPage,
  resetDieToolData,
  resetDieToolList,
  resetToolsList,
} from '../../slices/die-tool';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { ReactComponent as ViewIcon } from '../../Images/ViewIcon.svg';
import { fetchDieProfileById } from '../../slices/die-profile';
import { CSVLink } from 'react-csv';
import { font } from './../../Common/font';
import dayjs from 'dayjs';
import moment from 'moment';
import 'jspdf-autotable';
import jsPDF from 'jspdf';
import { fetchWorkOrderReport } from '../../slices/work-order';
import * as XLSX from 'xlsx';
import logo from '../../Images/logo.svg';

const ViewDieProfile = () => {
  let navigate = useNavigate();
  const { id } = useParams();
  const printRef = useRef();
  const dispatch = useDispatch();
  const [arrow, setArrow] = useState('Show');
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const debounceTimeout = useRef(null);
  const [dieProfile, setDieProfile] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [popupData, setPopupData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { user: currentUser } = useSelector(state => state.auth);
  const { data: settingData } = useSelector(state => state.setting);
  const { toolsList, totalRecord } = useSelector(state => state.dieTool);
  const [orderData, setOrderData] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(100);

  const fetchWorkOrderData = (die_id = id) => {
    setOrderLoading(true);

    const params = {
      page,
      pagesize,
      die_id: die_id,
    };

    dispatch(fetchWorkOrderReport(params))
      .then(response => {
        setOrderData(response?.payload?.data || []);
      })
      .catch(error => {
        console.error('Error fetching work order report:', error);
        setOrderData([]);
        message.error('Failed to fetch work order report');
      })
      .finally(() => {
        setOrderLoading(false);
      });
  };

  useEffect(() => {
    fetchWorkOrderData(id);
  }, [id]);

  useEffect(() => {
    dispatch(resetToolsList());
    dispatch(
      fetchDieToolByDieProfileId({
        dieId: id,
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [page, orderBy]);

  const mergedArrow = useMemo(() => {
    if (arrow === 'Hide') {
      return false;
    }
    if (arrow === 'Show') {
      return true;
    }
    return {
      pointAtCenter: true,
    };
  }, [arrow]);

  useEffect(() => {
    const fetchDieProfile = async () => {
      try {
        dispatch(fetchDieProfileById(id))
          .unwrap()
          .then(response => {
            if (response.success) {
              setDieProfile(response?.data);
            }
          })
          .catch(error => {
            console.error(error);
          });
      } catch (error) {
        console.error(error);
      }
    };
    fetchDieProfile();
  }, [id]);

  const handleEdit = data => {
    navigate(`/edit-die-tool/${data.id}`);
  };

  const deviationStatusMap = {
    active: 'Active',
    inactive: 'Inactive',
    die_broken: 'Die Broken',
    die_absolute: 'Die Absolute',
    die_shift: 'Die Shift',
    other: 'Other',
  };

  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'is_active',
    'purchase_price',
    'ownership',
    'run_under_deviation',
    'developer',
    'total_running_qty',
    'die_cavity',
    'order_date',
    'received_date',
    'die_size',
    'tool_number',
    'current_kg',
    'drawing_no',
    'developer_ref_no',
    'total_running_ton',
    'rac_no',
    'row_no',
    'column_no',
    'remarks',
    'id',
    'action',
  ]);
  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };

  function addRowSpan(data, key) {
    const newData = data.map(item => ({ ...item })); // clone each object
    let prevValue = null;
    let startIndex = 0;
    let count = 0;

    for (let i = 0; i <= newData.length; i++) {
      const currValue = newData[i]?.[key];

      if (currValue !== prevValue) {
        if (count > 0) {
          newData[startIndex].rowSpan = count;
          for (let j = startIndex + 1; j < i; j++) {
            newData[j].rowSpan = 0;
          }
        }
        prevValue = currValue;
        startIndex = i;
        count = 1;
      } else {
        count++;
      }
    }

    return newData;
  }

  const sortedData = [...orderData].sort((a, b) =>
    a.customer_name.localeCompare(b.customer_name),
  );
  const dataSource = addRowSpan(sortedData, 'customer_name');

  const workOrderColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'sr_no',
      key: 'sr_no',
      render: (text, record, index) => index + 1,
      width: 60,
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 120,
      render: (value, row, index) => {
        // Apply the same customer grouping logic
        const customerSpanInfo = row.customerSpanInfo;
        const showCustomerCell =
          customerSpanInfo && customerSpanInfo.firstIndex === index;

        if (showCustomerCell) {
          return {
            children: value,
            props: {
              rowSpan: customerSpanInfo.count,
            },
          };
        }

        // If not the first row of the group, return with rowSpan 0 to hide
        return {
          children: value,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: 'WorkOrder No / Date',
      dataIndex: 'workorder_no',
      key: 'workorder_no',
      width: 120,
      render: (value, row, index) => {
        // Apply the same group spanning logic for workorder_no
        const groupSpanInfo = row.groupSpanInfo;
        const showGroupedCell =
          groupSpanInfo && groupSpanInfo.firstIndex === index;

        if (showGroupedCell) {
          return {
            children: value,
            props: {
              rowSpan: groupSpanInfo.count,
            },
          };
        }

        return {
          children: value,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: 'PO NO / Date',
      dataIndex: 'po_no',
      key: 'po_no',
      width: 120,
      render: (value, row, index) => {
        // Apply the same group spanning logic for po_no
        const groupSpanInfo = row.groupSpanInfo;
        const showGroupedCell =
          groupSpanInfo && groupSpanInfo.firstIndex === index;

        if (showGroupedCell) {
          return {
            children: value,
            props: {
              rowSpan: groupSpanInfo.count,
            },
          };
        }

        return {
          children: value,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: 'Profile No',
      dataIndex: 'section',
      key: 'section',
      width: 100,
      align: 'center',
      render: (value, row, index) => {
        // Apply the same group spanning logic for section
        const groupSpanInfo = row.groupSpanInfo;
        const showGroupedCell =
          groupSpanInfo && groupSpanInfo.firstIndex === index;

        if (showGroupedCell) {
          return {
            children: value,
            props: {
              rowSpan: groupSpanInfo.count,
            },
          };
        }

        return {
          children: value,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: 'Profile Image',
      dataIndex: 'profile_image',
      key: 'profile_image',
      width: 100,
      render: (profile_image, row, index) => {
        // Apply the same group spanning logic for profile_image
        const groupSpanInfo = row.groupSpanInfo;
        const showGroupedCell =
          groupSpanInfo && groupSpanInfo.firstIndex === index;

        if (showGroupedCell) {
          const imageContent =
            profile_image && profile_image.startsWith('https://') ? (
              <img
                width="70"
                src={profile_image}
                alt="Profile Image"
                style={{ objectFit: 'contain', maxHeight: '100%' }}
              />
            ) : null;

          return {
            children: imageContent,
            props: {
              rowSpan: groupSpanInfo.count,
            },
          };
        }

        return {
          children: null,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: 'Customer Ref No',
      dataIndex: 'customer_reference_number',
      key: 'customer_reference_number',
      width: 140,
      align: 'center',
    },
    {
      title: 'Length (mm)',
      dataIndex: 'length',
      key: 'length',
      width: 100,
      align: 'center',
    },
    {
      title: 'Order',
      children: [
        {
          title: 'PCS',
          dataIndex: 'order_pcs',
          key: 'order_pcs',
          width: 80,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'order_kgs',
          key: 'order_kgs',
          width: 80,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Packed',
      children: [
        {
          title: 'PCS',
          dataIndex: 'packed_pcs',
          key: 'packed_pcs',
          width: 80,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'packed_kgs',
          key: 'packed_kgs',
          width: 80,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(2) : ''),
        },
      ],
    },
    {
      title: 'Warehouse',
      children: [
        {
          title: 'PCS',
          dataIndex: 'warehouse_pcs',
          key: 'warehouse_pcs',
          width: 80,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'warehouse_kgs', // Fixed: was 'warehouse_pcs' in original
          key: 'warehouse_kgs',
          width: 80,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(2) : ''),
        },
      ],
    },
    {
      title: 'Dispatched',
      children: [
        {
          title: 'PCS',
          dataIndex: 'dispatched_pcs',
          key: 'dispatched_pcs',
          width: 80,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'dispatched_kgs',
          key: 'dispatched_kgs',
          width: 80,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(2) : ''),
        },
      ],
    },
    {
      title: 'Pending',
      children: [
        {
          title: 'PCS',
          dataIndex: 'pending_pcs',
          key: 'pending_pcs',
          width: 80,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'pending_kgs',
          key: 'pending_kgs',
          width: 80,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(2) : ''),
        },
      ],
    },
    {
      title: 'Remarks',
      dataIndex: 'remark',
      key: 'remark',
      width: 150,
      ellipsis: true,
      render: text => (
        <Tooltip title={text}>
          <span>{text || ''}</span>
        </Tooltip>
      ),
    },
  ];

  const processTableData = orderData => {
    // Sort data exactly like in print function
    const sortedData = [...orderData].sort((a, b) => {
      return (
        a.customer_name.localeCompare(b.customer_name) ||
        a.workorder_no.localeCompare(b.workorder_no) ||
        a.po_no.localeCompare(b.po_no) ||
        a.section.localeCompare(b.section) ||
        (a.profile_image || '').localeCompare(b.profile_image || '')
      );
    });

    // Calculate customer span map exactly like in print function
    const customerSpanMap = {};
    sortedData.forEach((item, index) => {
      const name = item.customer_name;
      if (!customerSpanMap[name]) {
        customerSpanMap[name] = {
          count: 1,
          firstIndex: index,
        };
      } else {
        customerSpanMap[name].count++;
      }
    });

    // Calculate group span map exactly like in print function
    const groupSpanMap = {};
    let currentCustomer = null;
    sortedData.forEach((item, index) => {
      if (item.customer_name !== currentCustomer) {
        currentCustomer = item.customer_name;
      }
      const key = `${currentCustomer}|${item.workorder_no}|${item.po_no}|${item.section}|${item.profile_image || 'N/A'}`;
      if (!groupSpanMap[key]) {
        groupSpanMap[key] = {
          count: 1,
          firstIndex: index,
        };
      } else {
        groupSpanMap[key].count++;
      }
    });

    // Add span information to each row
    return sortedData.map((order, index) => {
      const customerSpanInfo = customerSpanMap[order.customer_name];
      const groupKey = `${order.customer_name}|${order.workorder_no}|${order.po_no}|${order.section}|${order.profile_image || 'N/A'}`;
      const groupSpanInfo = groupSpanMap[groupKey];

      return {
        ...order,
        customerSpanInfo,
        groupSpanInfo,
      };
    });
  };

  const processedData = useMemo(() => {
    return processTableData(orderData);
  }, [orderData]);
  // Usage example:
  // const processedData = processTableData(orderData);
  // Then use processedData as your table dataSource

  const csvheaders = [
    { label: 'Tool No.', key: 'tool_number' },
    { label: 'Tool Size', key: 'die_size' },
    { label: 'Current KG/MTR', key: 'current_kg' },
    { label: 'Order Date', key: 'order_date' },
    { label: 'Received Date', key: 'received_date' },
    { label: 'Die Cavity', key: 'die_cavity' },
    { label: 'Die Received Status', key: 'status' },
    { label: 'Total Running QTY (kg)', key: 'total_running_qty' },
    { label: 'Rac No.', key: 'rac_no' },
    { label: 'Row No.', key: 'row_no' },
    { label: 'Column No.', key: 'column_no' },
    { label: 'Vendor', key: 'developer' },
    { label: 'Ownership', key: 'ownership' },
    { label: 'Drawing No.', key: 'drawing_no' },
    { label: 'Total Running Metric Ton', key: 'total_running_ton' },
    { label: 'Developer Ref. No', key: 'developer_ref_no' },
    { label: 'Run Under Deviation', key: 'run_under_deviation' },
    { label: 'Purchase Price (INR)', key: 'purchase_price' },
    { label: 'Remarks', key: 'remarks' },
    { label: 'Created At', key: 'created_at' },
    { label: 'Created By', key: 'created_by' },
    { label: 'Updated At', key: 'updated_at' },
    { label: 'Updated By', key: 'updated_by' },
  ];

  const csvFilterheaders = csvheaders.filter(column =>
    checkedList.includes(column.key),
  );

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      render: (text, record, index) => (page - 1) * pagesize + index + 1,
      sorter: true,
    },
    {
      title: 'Tool No.',
      dataIndex: 'tool_number',
      key: 'tool_number',
      sorter: true,
      render: (text, record) => (
        <Tooltip placement="top" title={'View Die Life Card'}>
          <Link to={`/die-life-card/${record.id}`} target="_blank">
            {record.die.die_number} - {record.tool_number}
          </Link>
        </Tooltip>
      ),
    },
    {
      title: 'Tool Size',
      dataIndex: 'die_size',
      key: 'die_size',
      sorter: true,
      render: record => {
        return (
          <span>
            {record?.die_width + ' MM X ' + record?.die_height + ' MM'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Current KG/MTR',
      dataIndex: 'current_kg',
      key: 'current_kg',
      sorter: true,
      render: (text, record) => {
        return (
          <span>
            {record && record?.current_kg
              ? record.current_kg
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Order Date',
      dataIndex: 'order_date',
      key: 'order_date',
      sorter: true,
      render: record => {
        return (
          <span>
            {record !== null ? dayjs(record).format('DD-MM-YYYY') : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Received Date',
      dataIndex: 'received_date',
      key: 'received_date',
      sorter: true,
      render: record => {
        return (
          <span>
            {record !== null ? dayjs(record).format('DD-MM-YYYY') : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Die Cavity',
      dataIndex: 'die_cavity',
      key: 'die_cavity',
      sorter: true,
    },
    {
      title: 'Die Received Status',
      dataIndex: 'is_active',
      key: 'is_active',
      sorter: true,
      render: status => (status === 'True' ? 'Received' : 'Not Received'),
    },
    {
      title: 'Total Running QTY (Kg)',
      dataIndex: 'total_running_qty',
      key: 'total_running_qty',
      sorter: true,
    },
    {
      title: 'Rac No.',
      dataIndex: 'rac_no',
      key: 'rac_no',
      sorter: true,
      render: (text, record) => {
        return <span>{record.rac_no !== null ? record.rac_no : 'N/A'} </span>;
      },
    },
    {
      title: 'Row No.',
      dataIndex: 'row_no',
      key: 'row_no',
      sorter: true,
      render: (text, record) => {
        return <span>{record.row_no !== null ? record.row_no : 'N/A'} </span>;
      },
    },
    {
      title: 'Column No.',
      dataIndex: 'column_no',
      key: 'column_no',
      sorter: true,
      render: (text, record) => {
        return (
          <span>{record.column_no !== null ? record.column_no : 'N/A'} </span>
        );
      },
    },
    {
      title: 'Vendor',
      dataIndex: 'developer',
      key: 'developer',
      render: (text, record) => {
        return (
          <span>
            {record.developer !== null
              ? record.developer.customer_name
              : 'N/A'}{' '}
          </span>
        );
      },
      sorter: true,
    },
    {
      title: 'Ownership',
      dataIndex: 'ownership',
      key: 'ownership',
      sorter: true,
      render: (text, record) => {
        return (
          <span>
            {record?.ownership === "own"  ? settingData?.company_settings?.company_name : record.ownership|| 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Drawing No.',
      dataIndex: 'drawing_no',
      key: 'drawing_no',
      sorter: true,
    },
    {
      title: 'Total Running Metric Ton',
      dataIndex: 'total_running_ton',
      key: 'total_running_ton',
      sorter: true,
    },
    {
      title: 'Developer Ref. No',
      dataIndex: 'developer_ref_no',
      key: 'developer_ref_no',
      sorter: true,
    },
    {
      title: 'Run Under Deviation',
      dataIndex: 'run_under_deviation',
      key: 'run_under_deviation',
      sorter: true,
      render: (text, record) => {
        if (record.run_under_deviation === 'inactive') {
          return (
            <span>
              {' '}
              {record.run_under_deviation
                ? deviationStatusMap[record.run_under_deviation] || 'Unknown'
                : 'N/A'}
              -{' '}
              {deviationStatusMap[record.run_under_deviation_inactive] ===
                'Other'
                ? record.run_under_deviation_reason
                : deviationStatusMap[record.run_under_deviation_inactive]}{' '}
            </span>
          );
        } else if (record.run_under_deviation === 'under development') {
          return (
            <span>
              {' '}
              {record?.run_under_deviation
                ? record.run_under_deviation || 'Unknown'
                : 'N/A'}{' '}
            </span>
          );
        } else {
          return (
            <span>
              {' '}
              {record.run_under_deviation
                ? deviationStatusMap[record.run_under_deviation] || 'Unknown'
                : 'N/A'}{' '}
            </span>
          );
        }
      },
    },
    {
      title: 'Purchase Price',
      dataIndex: 'purchase_price',
      key: 'purchase_price',
      sorter: true,
      render: (text, record) => {
        return (
          <span>
            {record.purchase_price}
          </span>
        );
      },
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      sorter: true,
      render: remarks => {
        return <span>{remarks ? remarks : 'N/A'} </span>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      render: record => {
        return <span>{dayjs(record).format('DD-MM-YYYY HH:mm')} </span>;
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? `${record.first_name} ${record.last_name}` : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      render: record => {
        return <span>{dayjs(record).format('DD-MM-YYYY HH:mm')} </span>;
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? `${record.first_name} ${record.last_name}` : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Die Life Card',
      dataIndex: 'id', // Use `id` here for proper alignment and access
      key: 'action',
      render: (text, record) => (
        <Space>
          {currentUser.permissions.permissions.die.includes('view_dietool') && (
            <Tooltip placement="top" title={'View Die Life Card'}>
              <Link to={`/die-life-card/${record?.id}`} target="_blank">
                <FileTextOutlined />
              </Link>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'id', // Use `id` here for proper alignment and access
      key: 'action',
      render: (text, record) => (
        <Space>
          {currentUser.permissions.permissions.die.includes('view_dietool') && (
            <Tooltip placement="top" title={'View Die Life Card'}>
              <Link to={`/die-life-card/${record.id}`} target="_blank">
                <ViewIcon />
              </Link>
            </Tooltip>
          )}
          {currentUser.permissions.permissions.die.includes(
            'change_dietool',
          ) && (
              <Tooltip placement="top" title={'Edit'}>
                <span onClick={() => handleEdit(record)}>
                  <EditIcon />
                </span>
              </Tooltip>
            )}
        </Space>
      ),
    },
  ];

  const ballonDrawingDimensionColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'ballon_no',
      key: 'ballon_no',
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Dimension',
      dataIndex: 'dim_type',
      key: 'dim_type',
    },
    {
      title: 'Nominal Value',
      dataIndex: 'nominal_value',
      key: 'nominal_value',
    },
    {
      title: '+ Tolerance (mm)',
      dataIndex: 'tolerance_plus',
      key: 'tolerance_plus',
      align: 'center',
    },
    {
      title: '- Tolerance (mm)',
      dataIndex: 'tolerance_minus',
      key: 'tolerance_minus',
      align: 'center',
    },
    {
      title: 'Min',
      dataIndex: 'min_value',
      key: 'min_value',
      align: 'center',
    },
    {
      title: 'Max',
      dataIndex: 'max_value',
      key: 'max_value',
      align: 'center',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      align: 'center',
    },
    {
      title: 'X Position',
      dataIndex: 'pos_x',
      key: 'pos_x',
      align: 'center',
    },
    {
      title: 'Y Position',
      dataIndex: 'pos_y',
      key: 'pos_y',
      align: 'center',
    },
    {
      title: 'Is Inspection',
      dataIndex: 'is_inspection',
      key: 'is_inspection',
      align: 'center',
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: 'Is Critical',
      dataIndex: 'is_critical',
      key: 'is_critical',
      align: 'center',
      render: (value) => (value ? "Yes" : "No"),
    }
  ];

  const handleSearch = e => {
    const value = e.target.value;
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    dispatch(resetToolsList());
    dispatch(resetDieToolData());
    dispatch(resetDieToolList());

    if (value) {
      setSearch(value);
      debounceTimeout.current = setTimeout(() => {
        dispatch(
          fetchDieToolByDieProfileId({
            dieId: id,
            page,
            pagesize,
            search: value.trim(), // Trim to avoid space-related issues
          }),
        );
      }, 100);
    } else {
      setSearch('');
      debounceTimeout.current = setTimeout(() => {
        dispatch(resetCurrentToolPage());
        dispatch(fetchDieToolByDieProfileId({ dieId: id, page, pagesize: 10 }));
      }, 100);
    }
  };

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';

    if (sortField === 'tool_number' && sortOrder === 'ascend') {
      sortingEnumKey = 'tool_number';
    }
    if (sortField === 'tool_number' && sortOrder === 'descend') {
      sortingEnumKey = '-tool_number';
    }
    if (sortField === 'die_size' && sortOrder === 'ascend') {
      sortingEnumKey = 'die_size';
    }
    if (sortField === 'die_size' && sortOrder === 'descend') {
      sortingEnumKey = '-die_size';
    }
    if (sortField === 'current_kg' && sortOrder === 'ascend') {
      sortingEnumKey = 'current_kg';
    }
    if (sortField === 'current_kg' && sortOrder === 'descend') {
      sortingEnumKey = '-current_kg';
    }
    if (sortField === 'die_cavity' && sortOrder === 'ascend') {
      sortingEnumKey = 'die_cavity';
    }
    if (sortField === 'die_cavity' && sortOrder === 'descend') {
      sortingEnumKey = '-die_cavity';
    }
    if (sortField === 'is_active' && sortOrder === 'ascend') {
      sortingEnumKey = 'is_active';
    }
    if (sortField === 'is_active' && sortOrder === 'descend') {
      sortingEnumKey = '-is_active';
    }
    if (sortField === 'die_sub_category' && sortOrder === 'ascend') {
      sortingEnumKey = 'die_sub_category';
    }
    if (sortField === 'die_sub_category' && sortOrder === 'descend') {
      sortingEnumKey = '-die_sub_category';
    }
    if (sortField === 'total_running_qty' && sortOrder === 'ascend') {
      sortingEnumKey = 'total_running_qty';
    }
    if (sortField === 'total_running_qty' && sortOrder === 'descend') {
      sortingEnumKey = '-total_running_qty';
    }
    if (sortField === 'rac_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'rac_no';
    }
    if (sortField === 'rac_no' && sortOrder === 'descend') {
      sortingEnumKey = '-rac_no';
    }
    if (sortField === 'row_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'row_no';
    }
    if (sortField === 'row_no' && sortOrder === 'descend') {
      sortingEnumKey = '-row_no';
    }
    if (sortField === 'column_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'column_no';
    }
    if (sortField === 'column_no' && sortOrder === 'descend') {
      sortingEnumKey = '-column_no';
    }
    if (sortField === 'developer' && sortOrder === 'ascend') {
      sortingEnumKey = 'developer';
    }
    if (sortField === 'developer' && sortOrder === 'descend') {
      sortingEnumKey = '-developer';
    }
    if (sortField === 'run_under_deviation' && sortOrder === 'ascend') {
      sortingEnumKey = 'run_under_deviation';
    }
    if (sortField === 'run_under_deviation' && sortOrder === 'descend') {
      sortingEnumKey = '-run_under_deviation';
    }
    if (sortField === 'purchase_price' && sortOrder === 'ascend') {
      sortingEnumKey = 'purchase_price';
    }
    if (sortField === 'purchase_price' && sortOrder === 'descend') {
      sortingEnumKey = '-purchase_price';
    }
    if (sortField === 'remarks' && sortOrder === 'ascend') {
      sortingEnumKey = 'remarks';
    }
    if (sortField === 'remarks' && sortOrder === 'descend') {
      sortingEnumKey = '-remarks';
    }
    if (sortField === 'drawing_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'drawing_no';
    }
    if (sortField === 'drawing_no' && sortOrder === 'descend') {
      sortingEnumKey = '-drawing_no';
    }
    if (sortField === 'total_running_ton' && sortOrder === 'ascend') {
      sortingEnumKey = 'total_running_ton';
    }
    if (sortField === 'total_running_ton' && sortOrder === 'descend') {
      sortingEnumKey = '-total_running_ton';
    }
    if (sortField === 'developer_ref_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'developer_ref_no';
    }
    if (sortField === 'developer_ref_no' && sortOrder === 'descend') {
      sortingEnumKey = '-developer_ref_no';
    }
    return sortingEnumKey;
  };
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setPageSize(pagination.pageSize);
    setOrderBy(orderBy);
  };

  const loadImageAsBase64 = url => {
    return new Promise((resolve, reject) => {
      const cacheBustUrl = `${url}${url.includes('?') ? '&' : '?'}nocache=${new Date().getTime()}`;

      const img = new Image();
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg'));
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = cacheBustUrl;
    });
  };

  const downloadPdf = async () => {
    const pdf = new jsPDF('l', 'pt', 'a4');
    pdf.addFileToVFS('WorkSans-normal.ttf', font);
    pdf.addFont('WorkSans-normal.ttf', 'WorkSans', 'normal');
    pdf.setFont('WorkSans');

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const marginLeft = 40;
    const marginRight = 40;
    let yPosition = 40;

    // Load profile image
    let profileImageBase64 = null;
    if (dieProfile?.die_diagram) {
      const fileUrl = dieProfile.die_diagram;
      const extension = getFileExtension(fileUrl).toUpperCase();
      const validImageFormats = ['PNG', 'JPEG', 'JPG'];

      if (validImageFormats.includes(extension)) {
        try {
          profileImageBase64 = await loadImageAsBase64(fileUrl);
        } catch (error) {
          console.error(`Failed to load profile image:`, error);
        }
      }
    }

    // Load tool images
    const imagePromises = toolsList.map(async item => {
      if (!item?.die_diagram) return { id: item.id, base64: null };

      const fileUrl = item.die_diagram;
      const extension = getFileExtension(fileUrl).toUpperCase();
      const validImageFormats = ['PNG', 'JPEG', 'JPG'];

      if (validImageFormats.includes(extension)) {
        try {
          const base64Image = await loadImageAsBase64(fileUrl);
          return { id: item.id, base64: base64Image, extension };
        } catch (error) {
          console.error(`Failed to load image for ${item.die_number}:`, error);
          return { id: item.id, base64: null };
        }
      }

      return { id: item.id, base64: null };
    });

    const loadedImages = await Promise.all(imagePromises);
    const imageMap = loadedImages.reduce((map, img) => {
      map[img.id] = img;
      return map;
    }, {});

    // Title
    pdf.setFontSize(18);
    pdf.setFont('WorkSans', 'bold');
    pdf.text('Profile Details', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 50;

    // Profile details - Complete list matching JSX
    const profileDetails = [
      { label: 'Profile Number:', value: dieProfile?.die_number || 'N/A' },
      {
        label: 'Customer Ref No:',
        value: dieProfile?.customer_reference_number || 'N/A',
      },
      {
        label: 'Profile Description:',
        value:
          [
            dieProfile?.dimension1 &&
            `${Number(dieProfile.dimension1).toFixed(2)} MM`,
            dieProfile?.dimension2 &&
            `${Number(dieProfile.dimension2).toFixed(2)} MM`,
            dieProfile?.dimension3 &&
            `${Number(dieProfile.dimension3).toFixed(2)} MM`,
            dieProfile?.dimension4 &&
            `${Number(dieProfile.dimension4).toFixed(2)} MM`,
          ]
            .filter(Boolean)
            .join(' X ') || 'N/A',
      },
      {
        label: 'Profile Group:',
        value:
          dieProfile?.die_group !== null
            ? dieProfile?.die_group?.name || 'N/A'
            : 'None',
      },
      {
        label: 'Profile Category:',
        value:
          dieProfile?.die_category !== null
            ? dieProfile?.die_category?.name || 'N/A'
            : 'N/A',
      },
      {
        label: 'Profile SubCategory:',
        value:
          dieProfile?.die_sub_category !== null &&
            dieProfile?.die_sub_category?.name !== ''
            ? dieProfile?.die_sub_category?.name
            : 'N/A',
      },
      {
        label: 'Calculated Kg/Mtr:',
        value: String(dieProfile?.wt_kg_p_mt || 'N/A'),
      },
      {
        label: 'Weight Range Kg/Mtr: ',
        value:
          dieProfile?.min_wt_kg_p_mt !== undefined &&
            dieProfile?.max_wt_kg_p_mt !== undefined
            ? `${Number(dieProfile.min_wt_kg_p_mt).toFixed(2)}-${Number(dieProfile.max_wt_kg_p_mt).toFixed(2)}`
            : 'N/A',
      },
      {
        label: 'Profile Type:',
        value: dieProfile?.die_type !== null ? dieProfile?.die_type : 'N/A',
      },
      {
        label: 'Total Running Km:',
        value:
          dieProfile?.total_running_ton !== null
            ? String(dieProfile?.total_running_ton)
            : 'N/A',
      },
      {
        label: 'Total Running Quantity:',
        value:
          dieProfile?.total_running_qty !== null
            ? String(dieProfile?.total_running_qty)
            : '0.0',
      },
      {
        label: 'Die Detail Diagram:',
        value:
          dieProfile?.die_detail_diagram !== null
            ? 'Available - View in system'
            : 'N/A',
      },
      {
        label: 'Customer Approved Die:',
        value:
          dieProfile?.customer_approved_diagram !== null
            ? 'Available - View in system'
            : 'N/A',
      },
      {
        label: 'Autocad Drawing:',
        value:
          dieProfile?.autocad_drawing !== null
            ? 'Available - Download in system'
            : 'N/A',
      },
      {
        label: 'Die Manufacturing:',
        value:
          dieProfile?.die_manufacturing !== null
            ? 'Available - Download in system'
            : 'N/A',
      },
      {
        label: 'SOP:',
        value:
          dieProfile?.die_sop !== null ? 'Available - View in system' : 'N/A',
      },
      {
        label: 'Remarks:',
        value: dieProfile?.remarks !== null ? dieProfile?.remarks : 'N/A',
      },
    ];

    pdf.setFontSize(10);

    // 3-column layout configuration
    const columnWidth = 220;
    const columnSpacing = 60;
    const labelWidth = 110;
    const valueWidth = 120;

    const column1X = marginLeft;
    const column2X = marginLeft + columnWidth + columnSpacing;
    const column3X = marginLeft + 2 * (columnWidth + columnSpacing);

    // Split profile details into two columns (first 2 columns for text details)
    const textDetails = profileDetails.slice(
      0,
      Math.ceil((profileDetails.length * 2) / 3),
    );

    let maxYPosition = yPosition;

    // Draw profile details in first two columns
    textDetails.forEach((detail, index) => {
      const isFirstColumn = index % 2 === 0;
      const xPosition = isFirstColumn ? column1X : column2X;
      const currentY = yPosition + Math.floor(index / 2) * 25;

      // Check if we need a new page
      if (currentY > pageHeight - 100) {
        pdf.addPage();
        yPosition = 40;
        maxYPosition = yPosition;
        const newCurrentY = yPosition + Math.floor(index / 2) * 25;

        // Label
        pdf.setFont('WorkSans', 'bold');
        pdf.text(detail.label, xPosition, newCurrentY);

        // Value (with text wrapping for long values)
        pdf.setFont('WorkSans', 'normal');
        const textLines = pdf.splitTextToSize(detail.value, valueWidth);
        pdf.text(textLines, xPosition + labelWidth, newCurrentY);

        maxYPosition = Math.max(
          maxYPosition,
          newCurrentY + (textLines.length - 1) * 12,
        );
      } else {
        // Label
        pdf.setFont('WorkSans', 'bold');
        pdf.text(detail.label, xPosition, currentY);

        // Value (with text wrapping for long values)
        pdf.setFont('WorkSans', 'normal');
        const textLines = pdf.splitTextToSize(detail.value, valueWidth);
        pdf.text(textLines, xPosition + labelWidth, currentY);

        maxYPosition = Math.max(
          maxYPosition,
          currentY + (textLines.length - 1) * 12,
        );
      }
    });

    // Add profile image in the third column
    if (profileImageBase64) {
      pdf.setFont('WorkSans', 'bold');
      pdf.setFontSize(12);
      pdf.text('Profile Diagram:', column3X, yPosition);

      const imageY = yPosition + 20;
      const maxImageWidth = 100;
      const maxImageHeight = 100;

      try {
        const img = new Image();
        img.src = profileImageBase64;
        await new Promise(resolve => {
          img.onload = resolve;
          setTimeout(resolve, 3000);
        });

        let imgWidth = img.width;
        let imgHeight = img.height;

        // Scale image to fit within the third column
        if (imgWidth > maxImageWidth) {
          const ratio = maxImageWidth / imgWidth;
          imgWidth = maxImageWidth;
          imgHeight = imgHeight * ratio;
        }

        if (imgHeight > maxImageHeight) {
          const ratio = maxImageHeight / imgHeight;
          imgHeight = maxImageHeight;
          imgWidth = imgWidth * ratio;
        }

        pdf.addImage(
          profileImageBase64,
          'JPEG',
          column3X,
          imageY,
          imgWidth,
          imgHeight,
        );

        maxYPosition = Math.max(maxYPosition, imageY + imgHeight);
      } catch (error) {
        console.error('Failed to add profile image to PDF:', error);
        pdf.setFont('WorkSans', 'normal');
        pdf.setFontSize(10);
        pdf.text('Image could not be displayed', column3X, imageY);
        maxYPosition = Math.max(maxYPosition, imageY + 20);
      }
    } else {
      // Show "No Image Available" in third column
      pdf.setFont('WorkSans', 'bold');
      pdf.setFontSize(12);
      pdf.text('Profile Diagram:', column3X, yPosition);
      pdf.setFont('WorkSans', 'normal');
      pdf.setFontSize(10);
      pdf.text('No image available', column3X, yPosition + 20);
    }

    // Update yPosition to continue after the 3-column section
    yPosition = maxYPosition + 40;

    // Add separator line
    if (yPosition > pageHeight - 100) {
      pdf.addPage();
      yPosition = 40;
    }

    pdf.setLineWidth(0.5);
    pdf.line(marginLeft, yPosition, pageWidth - marginRight, yPosition);
    yPosition += 30;

    // Die Tools List section
    pdf.setFontSize(16);
    pdf.setFont('WorkSans', 'bold');
    pdf.text('Die Tools List', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 30;

    // Complete columns list matching your original code
    const allColumns = [
      { header: 'Tool No.', dataKey: 'toolNumber', field: 'tool_number' },
      { header: 'Tool Size', dataKey: 'toolSize', field: 'die_size' },
      { header: 'Current KG/MTR', dataKey: 'wt_kg_p_mt', field: 'wt_kg_p_mt' },
      { header: 'Order Date', dataKey: 'order_date', field: 'order_date' },
      {
        header: 'Received Date',
        dataKey: 'received_date',
        field: 'received_date',
      },
      { header: 'Die Cavity', dataKey: 'dieCavity', field: 'die_cavity' },
      { header: 'Die Received Status', dataKey: 'status', field: 'is_active' },
      {
        header: 'Total Running QTY (kg)',
        dataKey: 'runningQty',
        field: 'total_running_qty',
      },
      { header: 'Rac No.', dataKey: 'rac_no', field: 'rac_no' },
      { header: 'Row No.', dataKey: 'row_no', field: 'row_no' },
      { header: 'Column No.', dataKey: 'column_no', field: 'column_no' },
      { header: 'Vendor', dataKey: 'developer', field: 'developer' },
      { header: 'Ownership', dataKey: 'ownership', field: 'ownership' },
      { header: 'Drawing No.', dataKey: 'drawing_no', field: 'drawing_no' },
      {
        header: 'Total Running Metric Ton',
        dataKey: 'total_running_ton',
        field: 'total_running_ton',
      },
      {
        header: 'Developer Ref No.',
        dataKey: 'developer_ref_no',
        field: 'developer_ref_no',
      },
      {
        header: 'Run Under Deviation',
        dataKey: 'deviationStatus',
        field: 'run_under_deviation',
      },
      {
        header: 'Purchase Price (INR)',
        dataKey: 'price',
        field: 'purchase_price',
      },
      { header: 'Remarks', dataKey: 'remarks', field: 'remarks' },
    ];

    const columns = allColumns.filter(col => checkedList.includes(col.field));
    const rows = toolsList.map(item => {
      const row = {};
      columns.forEach(col => {
        switch (col.field) {
          case 'tool_number':
            row.toolNumber = item?.die?.die_number
              ? `${item.die.die_number}-${item.tool_number}`
              : 'N/A';
            break;
          case 'die_size':
            row.toolSize = item?.die_size?.die_width
              ? `${item.die_size.die_width} X ${item.die_size.die_height}`
              : 'N/A';
            break;
          case 'wt_kg_p_mt':
            row.wt_kg_p_mt = item?.die?.wt_kg_p_mt || 'N/A';
            break;
          case 'order_date':
            row.order_date = item?.order_date
              ? moment(item.order_date).format('DD-MM-YYYY')
              : 'N/A';
            break;
          case 'received_date':
            row.received_date = item?.received_date
              ? moment(item.received_date).format('DD-MM-YYYY')
              : 'N/A';
            break;
          case 'die_cavity':
            row.dieCavity = item?.die_cavity || 'N/A';
            break;
          case 'is_active':
            row.status = item?.is_active ? 'Active' : 'Inactive';
            break;
          case 'total_running_qty':
            row.runningQty = item?.total_running_qty?.toLocaleString() || '0';
            break;
          case 'developer':
            row.developer = item?.developer?.vendor_registered_name || 'N/A';
            break;
          case 'purchase_price':
            row.price = item?.purchase_price?.toLocaleString() || '0';
            break;
          case 'run_under_deviation':
            row.deviationStatus = item?.run_under_deviation ? 'Yes' : 'No';
            break;
          default:
            row[col.dataKey] = item[col.field] || 'N/A';
        }
      });
      return row;
    });

    // Add the table
    pdf.autoTable({
      columns: columns,
      body: rows,
      startY: yPosition,
      theme: 'grid',
      margin: { top: 40, right: marginRight, left: marginLeft, bottom: 40 },
      styles: {
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: 3.8,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
        fontSize: 8,
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: [240, 240, 240],
      },
      columnStyles: {
        price: { halign: 'right' },
        runningQty: { halign: 'right' },
      },
      didDrawPage: data => {
        // Add page numbers
        pdf.setFontSize(8);
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          pdf.text(
            `Page ${i} of ${pageCount}`,
            pageWidth - 60,
            pdf.internal.pageSize.getHeight() - 20,
          );
        }
      },
      willDrawCell: data => {
        // Truncate long text
        if (data.cell.text.length > 0) {
          const text = data.cell.text[0];
          if (text && text.length > 30) {
            data.cell.text[0] = text.substring(0, 27) + '...';
          }
        }
      },
    });

    // Save the PDF
    pdf.save(
      `profile_and_die_tool_${new Date().toISOString().split('T')[0]}.pdf`,
    );
  };

  const getFileExtension = url => {
    const parts = url.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : null;
  };
  const showModal = async (data, title) => {
    let extension = getFileExtension(data);
    const isDwg = extension === 'dwg';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
      extension,
    );
    if (isImage) {
      setModalTitle(title);
      setPopupData(data);
      setIsModalVisible(true);
    } else if (isDwg) {
      window.open(data, '_blank', 'noopener,noreferrer');
    } else {
      const response = await fetch(data);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const blobData = await response.blob();
      var blob = new Blob([blobData], { type: 'application/pdf' });
      var blobURL = URL.createObjectURL(blob);
      window.open(blobURL);
    }
  };
  const exportDieToolToPDF = data => {
    const pdf = new jsPDF('l', 'pt', 'a4');
    pdf.addFileToVFS('WorkSans-normal.ttf', font);
    pdf.addFont('WorkSans-normal.ttf', 'WorkSans', 'normal');
    pdf.setFont('WorkSans');
    const pageHeight = pdf.internal.pageSize.getHeight();

    const allColumns = [
      { header: 'Tool No.', dataKey: 'toolNumber', field: 'tool_number' },
      { header: 'Tool Size', dataKey: 'toolSize', field: 'die_size' },
      { header: 'Current KG/MTR', dataKey: 'wt_kg_p_mt', field: 'wt_kg_p_mt' },
      { header: 'Order Date', dataKey: 'order_date', field: 'order_date' },
      {
        header: 'Recived Date',
        dataKey: 'received_date',
        field: 'received_date',
      },

      { header: 'Die Cavity', dataKey: 'dieCavity', field: 'die_cavity' },
      { header: 'Die Received Status', dataKey: 'status', field: 'is_active' },
      {
        header: 'Total Running QTY (kg)',
        dataKey: 'runningQty',
        field: 'total_running_qty',
      },
      { header: 'Rac No.', dataKey: 'rac_no', field: 'rac_no' },
      { header: 'Row No.', dataKey: 'row_no', field: 'row_no' },
      { header: 'Column No.', dataKey: 'column_no', field: 'column_no' },
      { header: 'Vendor', dataKey: 'developer', field: 'developer' },
      { header: 'Ownership', dataKey: 'ownership', field: 'ownership' },
      { header: 'Drawing No.', dataKey: 'drawing_no', field: 'drawing_no' },
      {
        header: 'Total Running Metric Ton',
        dataKey: 'total_running_ton',
        field: 'total_running_ton',
      },
      {
        header: 'Developer Ref No.',
        dataKey: 'developer_ref_no',
        field: 'developer_ref_no',
      },
      {
        header: 'Run Under Deviation',
        dataKey: 'deviationStatus',
        field: 'run_under_deviation',
      },
      {
        header: 'Purchase Price (INR)',
        dataKey: 'price',
        field: 'purchase_price',
      },
      { header: 'Remarks', dataKey: 'remarks', field: 'remarks' },
      { header: 'Created At', dataKey: 'created_at', field: 'created_at' },
      { header: 'Created By', dataKey: 'created_by', field: 'created_by' },
      { header: 'Updated At', dataKey: 'updated_at', field: 'updated_at' },
      { header: 'Updated By', dataKey: 'updated_by', field: 'updated_by' },
    ];

    const columns = allColumns.filter(col => checkedList.includes(col.field));

    const rows = toolsList.map(item => {
      const row = {};
      columns.forEach(col => {
        switch (col.field) {
          case 'tool_number':
            row.toolNumber = item?.die?.die_number
              ? `${item.die.die_number}-${item.tool_number}`
              : 'N/A';
            break;
          case 'created_at':
            row.created_at = item.created_at
              ? moment(item.created_at).format('DD-MM-YYYY')
              : 'N/A';
            break;
          case 'created_by':
            row.created_by = item.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`
              : 'N/A';
            break;
          case 'updated_at':
            row.updated_at = item.updated_at
              ? moment(item.updated_at).format('DD-MM-YYYY')
              : 'N/A';
            break;
          case 'updated_by':
            row.updated_by = item.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
              : 'N/A';
            break;
          case 'wt_kg_p_mt':
            row.wt_kg_p_mt = item?.current_kg
              ? `${item.current_kg}`
              : 'N/A';
            break;
          case 'order_date':
            row.order_date = item?.order_date ? `${item.order_date}` : 'N/A';
            break;
          case 'received_date':
            row.received_date = item?.received_date
              ? `${item.received_date}`
              : 'N/A';
            break;
          case 'die_size':
            row.toolSize = item?.die_size.die_width
              ? `${item.die_size.die_width} X ${item.die_size.die_height}`
              : 'N/A';
            break;
          case 'die_cavity':
            row.dieCavity = item.die_cavity || 'N/A';
            break;
          case 'is_active':
            row.status = item.is_active ? true : false;
            break;
          case 'total_running_qty':
            row.runningQty = item.total_running_qty || '0';
            break;
          case 'rac_no':
            row.rac_no = item.rac_no || '0';
            break;
          case 'row_no':
            row.row_no = item.row_no || '0';
            break;
          case 'drawing_no':
            row.drawing_no = item.drawing_no || 'N/A';
            break;
          case 'total_running_ton':
            row.total_running_ton = item.total_running_ton || '0';
            break;
          case 'developer_ref_no':
            row.developer_ref_no = item.developer_ref_no || 'N/A';
            break;
          case 'column_no':
            row.column_no = item.column_no || '0';
            break;
          case 'developer':
            row.developer = item.developer.vendor_registered_name || 'N/A';
            break;
          case 'ownership':
            row.ownership = item?.ownership === "own" ? settingData?.company_settings?.company_name : item.ownership || 'N/A' ;
            break;
          case 'run_under_deviation':
            row.deviationStatus = item.run_under_deviation
              ? deviationStatusMap[item.run_under_deviation] || 'Unknown'
              : 'N/A';
            break;
          case 'purchase_price':
            row.price =
              `${item.purchase_price?.toLocaleString({ maximumFractionDigits: 0 })}` ||
              '0';
            break;
          case 'remarks':
            row.remarks = item.remarks || 'N/A';
            break;
        }
      });
      return row;
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    pdf.setFontSize(16);
    pdf.text('Die Tool', pageWidth / 2, 40, { align: 'center' });
    pdf.autoTable({
      columns,
      body: rows,
      startY: 80,
      theme: 'grid',
      margin: { top: 80, right: 30, left: 30, bottom: 40 },
      styles: {
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: 3.8,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
      },
      columnStyles: {
        price: { halign: 'right' },
        runningQty: { halign: 'right' },
      },
      didDrawPage: (data) => {
        const now = moment().format("DD-MM-YYYY hh:mm:ss A");
        if (data.pageNumber === 1) {
          pdf.addImage(logo, "PNG", pageWidth - 100, 10, 90, 30);
        }
        pdf.setFontSize(8);
        pdf.text(`${now}`, 40, pageHeight - 20);
        pdf.text(`Page ${data.pageNumber}`, pageWidth - 40, pageHeight - 20, { align: "right" });
      },
      willDrawCell: data => {
        if (data.cell.text.length > 0) {
          const text = data.cell.text[0];
          if (text && text.length > 30) {
            data.cell.text[0] = text.substring(0, 27) + '...';
          }
        }
      },
    });
    pdf.save(`die_tool${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const csvListData = toolsList?.map((item, index) => ({
    tool_number: item?.die?.die_number
      ? `${item.die.die_number}-${item.tool_number}`
      : 'N/A',
    wt_kg_p_mt: item?.current_kg ? `${item.current_kg}` : 'N/A',
    order_date: item?.order_date ? `${item.order_date}` : 'N/A',
    received_date: item?.received_date ? `${item.received_date}` : 'N/A',
    die_size: item?.die_size.die_width
      ? `${item.die_size.die_width} X ${item.die_size.die_height}`
      : 'N/A',
    die_cavity: item.die_cavity || 'N/A',
    status: item.is_active ? 'True' : 'False',
    total_running_qty: item.total_running_qty || '0',
    total_running_ton: item.total_running_ton || '0',
    developer_ref_no: item.developer_ref_no || 'N/A',
    drawing_no: item.drawing_no || 'N/A',
    rac_no: item.rac_no || '0',
    row_no: item.row_no || '0',
    column_no: item.column_no || '0',
    developer: item.developer.vendor_registered_name || 'N/A',
    ownership: item?.ownership === "own" ? settingData?.company_settings?.company_name : item.ownership || 'N/A' || 'N/A',
    run_under_deviation: item.run_under_deviation
      ? deviationStatusMap[item.run_under_deviation] || 'Unknown'
      : 'N/A',
    purchase_price:
      item.purchase_price?.toLocaleString({ maximumFractionDigits: 0 }) || '0',
    remarks: item.remarks || 'N/A',
    created_at: item.created_at
      ? moment(item.created_at).format('DD-MM-YYYY')
      : 'N/A',
    created_by: item.created_by
      ? `${item.created_by.first_name} ${item.created_by.last_name}`
      : 'N/A',
    updated_at: item.updated_at
      ? moment(item.updated_at).format('DD-MM-YYYY')
      : 'N/A',
    updated_by: item.updated_by
      ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
      : 'N/A',
  }));

  const handleCancel = () => {
    setIsModalVisible(false);
    setPopupData(null);
    setModalTitle('');
  };

  const resetFilter = () => {
    setSearch('');
    setOrderBy('');
    setPage(1);
  };

  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Checkbox value="id" disabled>
                Sr. No.
              </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="tool_number">Tool Number</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="die_size">Tool Size</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="current_kg">Current KG/MTR</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="die_cavity">Die Cavity</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="received_date">Recived Date</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="order_date">Order Date</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="is_active">Die Received Status</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="total_running_qty">
                Total Running QTY (kg)
              </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="developer">Vendor</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="ownership">Ownership</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="developer_ref_no">Developer Ref. No</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="total_running_ton">
                Total Running Metric Ton
              </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="rac_no">Rac Number</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="row_no">Row Number</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="column_no">Column Number</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="drawing_no">Drawing No.</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="remarks">Remarks</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="run_under_deviation">
                Run Under Deviation
              </Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="purchase_price">Purchase Price</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_at">Created At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_by">Created By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_at">Updated At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_by">Updated By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );
  const generateTableRows = () => {
    const sortedData = [...orderData].sort((a, b) => {
      return (
        a.customer_name.localeCompare(b.customer_name) ||
        a.workorder_no.localeCompare(b.workorder_no) ||
        a.po_no.localeCompare(b.po_no) ||
        a.section.localeCompare(b.section) ||
        (a.profile_image || '').localeCompare(b.profile_image || '')
      );
    });

    const customerSpanMap = {};
    sortedData.forEach((item, index) => {
      const name = item.customer_name;
      if (!customerSpanMap[name]) {
        customerSpanMap[name] = {
          count: 1,
          firstIndex: index,
        };
      } else {
        customerSpanMap[name].count++;
      }
    });

    const groupSpanMap = {};
    let currentCustomer = null;
    sortedData.forEach((item, index) => {
      if (item.customer_name !== currentCustomer) {
        currentCustomer = item.customer_name;
      }
      const key = `${currentCustomer}|${item.workorder_no}|${item.po_no}|${item.section}|${item.profile_image || 'N/A'}`;
      if (!groupSpanMap[key]) {
        groupSpanMap[key] = {
          count: 1,
          firstIndex: index,
        };
      } else {
        groupSpanMap[key].count++;
      }
    });

    return sortedData
      .map((order, index) => {
        const customerSpanInfo = customerSpanMap[order.customer_name];
        const showCustomerCell = customerSpanInfo.firstIndex === index;

        const groupKey = `${order.customer_name}|${order.workorder_no}|${order.po_no}|${order.section}|${order.profile_image || 'N/A'}`;
        const groupSpanInfo = groupSpanMap[groupKey];
        const showGroupedCells = groupSpanInfo.firstIndex === index;

        return `
        <tr>
          <td>${index + 1}</td>
          ${showCustomerCell ? `<td rowspan="${customerSpanInfo.count}">${order.customer_name || 'N/A'}</td>` : ''}
          ${showGroupedCells ? `<td rowspan="${groupSpanInfo.count}">${order.workorder_no || 'N/A'}</td>` : ''}
          ${showGroupedCells ? `<td rowspan="${groupSpanInfo.count}">${order.po_no || 'N/A'}</td>` : ''}
          ${showGroupedCells ? `<td rowspan="${groupSpanInfo.count}">${order.section || 'N/A'}</td>` : ''}
          ${showGroupedCells
            ? `<td rowspan="${groupSpanInfo.count}">
            ${order.profile_image && order.profile_image !== 'N/A'
              ? `<img src="${order.profile_image}" alt="Profile" class="profile-image" onerror="this.style.display='none';" />`
              : ' '
            }
          </td>`
            : ''
          }
          <td>${order.customer_reference_number || ''}</td>
          <td>${order.length || ''}</td>
          <td>${order.order_pcs || ''}</td>
          <td style="text-align: right;">${order.order_kgs ? parseFloat(order.order_kgs).toFixed(2) : ''}</td>
          <td>${order.packed_pcs || ''}</td>
          <td style="text-align: right;">${order.packed_kgs ? parseFloat(order.packed_kgs).toFixed(2) : ''}</td>
          <td>${order.warehouse_pcs || ''}</td>
          <td style="text-align: right;">${order.warehouse_kgs ? parseFloat(order.warehouse_kgs).toFixed(2) : ''}</td>
          <td>${order.dispatched_pcs || ''}</td>
          <td style="text-align: right;">${order.dispatched_kgs ? parseFloat(order.dispatched_kgs).toFixed(2) : ''}</td>
          <td>${order.pending_pcs || ''}</td>
          <td style="text-align: right;">${order.pending_kgs ? parseFloat(order.pending_kgs).toFixed(2) : ''}</td>
          <td>${order.remark || ''}</td>
        </tr>
      `;
      })
      .join('');
  };

  const handlePrint = () => {
    const now = new Date();
    const formattedDateTime = now.toLocaleString().replace(/"/g, '\\"'); // Escape quotes

    const windowPrint = window.open('', '', 'width=1200,height=800');

    windowPrint.document.write(`
  <!DOCTYPE html>
  <html>
    <head>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: Arial, sans-serif;
          font-size: 10px;
          line-height: 1.2;
          color: #333;
          background: white;
        }
        .print-container {
          padding: 10px;
          max-width: 100%;
        }
        .print-header {
          text-align: center;
          margin-bottom: 20px;
          position: relative;
        }
        .print-header h1 {
          font-size: 20px;
          font-weight: bold;
          color: #333;
        }
        .print-logo {
          position: absolute;
          top: 0;
          right: 0;
          width: 100px;
          height: auto;
        }
        .print-footer {
          position: fixed;
          bottom: 10px;
          left: 10px;
          font-size: 8px;
          color: #666;
          width: 100%;
        }
        @media print {
          .print-logo {
            display: block;
            position: running(logo);
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        .work-order-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
          font-size: 9px;
          border: 0.5px solid #333;
        }
        .work-order-table th,
        .work-order-table td {
          border: 1px solid #666;
          padding: 2px 3px;
          text-align: center;
          vertical-align: middle;
          word-wrap: break-word;
        }
        .work-order-table th {
          background-color: #12406a;
          color: white;
          font-weight: bold;
          font-size: 8px;
        }
        .total-row {
          font-weight: bold;
          font-size: 9px;
          border-top: 2px solid #333 !important;
        }
        .profile-image {
          max-width: 40px;
          max-height: 30px;
          display: block;
          margin: 0 auto;
        }
        @page {
          margin: 1in;
          @bottom-left {
            content: "${formattedDateTime}";
            font-size: 8px;
            color: #666;
          }
          @bottom-right {
            content: "Page " counter(page) " of " counter(pages);
            font-size: 8px;
            color: #666;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="print-header">
          <h1>Work Order Report</h1>
          <img src="${logo}" class="print-logo" />
        </div>
        <table class="work-order-table">
          <thead>
            <tr>
              <th rowspan="2">Sr.<br/>No.</th>
              <th rowspan="2">Customer<br/>Name</th>
              <th rowspan="2">Work Order No<br/>/Date</th>
              <th rowspan="2">PO No<br/>/Date</th>
              <th rowspan="2">Profile<br/>No</th>
              <th rowspan="2">Profile<br/>Image</th>
              <th rowspan="2">Customer Ref<br/>No</th>
              <th rowspan="2">Length<br/>(mm)</th>
              <th colspan="2">Order</th>
              <th colspan="2">Packed</th>
              <th colspan="2">Warehouse</th>
              <th colspan="2">Dispatched</th>
              <th colspan="2">Pending</th>
              <th rowspan="2">Remarks</th>
            </tr>
            <tr>
              <th>PCS</th>
              <th>KGS</th>
              <th>PCS</th>
              <th>KGS</th>
              <th>PCS</th>
              <th>KGS</th>
              <th>PCS</th>
              <th>KGS</th>
              <th>PCS</th>
              <th>KGS</th>
            </tr>
          </thead>
          <tbody>
            ${generateTableRows()}
            ${orderData.length > 0
        ? `
                <tr class="total-row">
                  <td colspan="8"><strong>Total</strong></td>
                  <td><strong>${orderData.reduce(
          (sum, o) => sum + (parseInt(o.order_pcs) || 0),
          0
        )}</strong></td>
                  <td><strong>${orderData
          .reduce((sum, o) => sum + (parseFloat(o.order_kgs) || 0), 0)
          .toFixed(2)}</strong></td>
                  <td><strong>${orderData.reduce(
            (sum, o) => sum + (parseInt(o.packed_pcs) || 0),
            0
          )}</strong></td>
                  <td><strong>${orderData
          .reduce((sum, o) => sum + (parseFloat(o.packed_kgs) || 0), 0)
          .toFixed(2)}</strong></td>
                  <td><strong>${orderData.reduce(
            (sum, o) => sum + (parseInt(o.warehouse_pcs) || 0),
            0
          )}</strong></td>
                  <td><strong>${orderData
          .reduce(
            (sum, o) => sum + (parseFloat(o.warehouse_kgs) || 0),
            0
          )
          .toFixed(2)}</strong></td>
                  <td><strong>${orderData.reduce(
            (sum, o) => sum + (parseInt(o.dispatched_pcs) || 0),
            0
          )}</strong></td>
                  <td><strong>${orderData
          .reduce(
            (sum, o) => sum + (parseFloat(o.dispatched_kgs) || 0),
            0
          )
          .toFixed(2)}</strong></td>
                  <td><strong>${orderData.reduce(
            (sum, o) => sum + (parseInt(o.pending_pcs) || 0),
            0
          )}</strong></td>
                  <td><strong>${orderData
          .reduce((sum, o) => sum + (parseFloat(o.pending_kgs) || 0), 0)
          .toFixed(2)}</strong></td>
                  <td></td>
                </tr>
              `
        : ''
      }
          </tbody>
        </table>
        <div class="print-footer">
          Generated on: ${formattedDateTime}
        </div>
      </div>
    </body>
  </html>
  `);

    windowPrint.document.close();
    windowPrint.focus();

    setTimeout(() => {
      windowPrint.print();
      windowPrint.close();
    }, 500);
  };

  const handleExcelDownload = () => {
    const sortedData = [...orderData].sort((a, b) => {
      return (
        a.customer_name.localeCompare(b.customer_name) ||
        a.workorder_no.localeCompare(b.workorder_no) ||
        a.po_no.localeCompare(b.po_no)
      );
    });

    // Calculate spans for customer_name
    const customerSpanMap = {};
    sortedData.forEach((item, index) => {
      const name = item.customer_name;
      if (!customerSpanMap[name]) {
        customerSpanMap[name] = {
          count: 1,
          firstIndex: index,
        };
      } else {
        customerSpanMap[name].count++;
      }
    });

    // Calculate spans for workorder_no and po_no
    const workOrderSpanMap = {};
    const poNoSpanMap = {};
    sortedData.forEach((item, index) => {
      // Work Order grouping
      const isWorkOrderRepeated =
        index > 0 && sortedData[index - 1].workorder_no === item.workorder_no;
      if (!isWorkOrderRepeated) {
        let count = 1;
        for (let i = index + 1; i < sortedData.length; i++) {
          if (sortedData[i].workorder_no === item.workorder_no) {
            count++;
          } else {
            break;
          }
        }
        workOrderSpanMap[item.workorder_no] = {
          count,
          firstIndex: index,
        };
      }

      // PO Number grouping
      const isPoNoRepeated =
        index > 0 &&
        sortedData[index - 1].workorder_no === item.workorder_no &&
        sortedData[index - 1].po_no === item.po_no;
      if (!isPoNoRepeated) {
        let count = 1;
        for (let i = index + 1; i < sortedData.length; i++) {
          if (
            sortedData[i].workorder_no === item.workorder_no &&
            sortedData[i].po_no === item.po_no
          ) {
            count++;
          } else {
            break;
          }
        }
        poNoSpanMap[`${item.workorder_no}|${item.po_no}`] = {
          count,
          firstIndex: index,
        };
      }
    });

    const wb = XLSX.utils.book_new();

    const wsData = [];

    const headers = [
      'Sr. No.',
      'Customer Name',
      'Work Order No / Date',
      'PO No / Date',
      'Profile No',
      'Customer Reference Number',
      'Length (mm)',
      'Order PCS',
      'Order KGS',
      'Packed PCS',
      'Packed KGS',
      'Warehouse PCS',
      'Warehouse KGS',
      'Dispatched PCS',
      'Dispatched KGS',
      'Pending PCS',
      'Pending KGS',
      'Remarks',
    ];

    wsData.push(headers);

    sortedData.forEach((order, index) => {
      const customerSpanInfo = customerSpanMap[order.customer_name];
      const showCustomerCell = customerSpanInfo.firstIndex === index;
      const isWorkOrderRepeated =
        index > 0 && sortedData[index - 1].workorder_no === order.workorder_no;
      const workOrderCell = isWorkOrderRepeated
        ? ''
        : `${order.workorder_no || ''}${order.order_date ? '\n' + dayjs(order.order_date).format('DD-MM-YYYY') : ''}`;

      const isPoNoRepeated =
        index > 0 &&
        sortedData[index - 1].workorder_no === order.workorder_no &&
        sortedData[index - 1].po_no === order.po_no;
      const poNoCell = isPoNoRepeated
        ? ''
        : `${order.po_no || ''}${order.po_date ? '\n' + dayjs(order.po_date).format('DD-MM-YYYY') : ''}`;

      const row = [
        index + 1,
        showCustomerCell ? order.customer_name || '' : '',
        workOrderCell,
        poNoCell,
        order.section || 'N/A',
        order.customer_reference_number || '',
        order.length || 'N/A',
        order.order_pcs || '',
        order.order_kgs ? parseFloat(order.order_kgs) : '',
        order.packed_pcs || '',
        order.packed_kgs ? parseFloat(order.packed_kgs) : '',
        order.warehouse_pcs || '',
        order.warehouse_kgs ? parseFloat(order.warehouse_kgs) : '',
        order.dispatched_pcs || '',
        order.dispatched_kgs ? parseFloat(order.dispatched_kgs) : '',
        order.pending_pcs || '',
        order.pending_kgs ? parseFloat(order.pending_kgs) : '',
        order.remark || '',
      ];

      wsData.push(row);
    });

    if (sortedData.length > 0) {
      const totalRowIndex = wsData.length + 1;
      const totalRow = [
        '',
        '',
        '',
        '',
        '',
        '',
        'TOTAL',
        { f: `SUM(H2:H${totalRowIndex - 1})` }, // Order PCS
        { f: `SUM(I2:I${totalRowIndex - 1})` }, // Order KGS
        { f: `SUM(J2:J${totalRowIndex - 1})` }, // Packed PCS
        { f: `SUM(K2:K${totalRowIndex - 1})` }, // Packed KGS
        { f: `SUM(L2:L${totalRowIndex - 1})` }, // Warehouse PCS
        { f: `SUM(M2:M${totalRowIndex - 1})` }, // Warehouse KGS
        { f: `SUM(N2:N${totalRowIndex - 1})` }, // Dispatched PCS
        { f: `SUM(O2:O${totalRowIndex - 1})` }, // Dispatched KGS
        { f: `SUM(P2:P${totalRowIndex - 1})` }, // Pending PCS
        { f: `SUM(Q2:Q${totalRowIndex - 1})` }, // Pending KGS
        '',
      ];
      wsData.push(totalRow);
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = 1; R <= range.e.r; ++R) {
      for (let C of [8, 10, 12, 14, 16]) {
        // H-Q numeric cols
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (ws[cellRef]) {
          ws[cellRef].z = '0.000'; // format with 3 decimals
        }
      }
    }
    const merges = [];

    // Merge cells for customer_name
    Object.entries(customerSpanMap).forEach(([customerName, spanInfo]) => {
      if (spanInfo.count > 1) {
        const startRow = spanInfo.firstIndex + 1;
        const endRow = startRow + spanInfo.count - 1;
        merges.push({
          s: { r: startRow, c: 1 },
          e: { r: endRow, c: 1 },
        });
      }
    });

    // Merge cells for workorder_no
    Object.entries(workOrderSpanMap).forEach(([workOrderNo, spanInfo]) => {
      if (spanInfo.count > 1) {
        const startRow = spanInfo.firstIndex + 1;
        const endRow = startRow + spanInfo.count - 1;
        merges.push({
          s: { r: startRow, c: 2 },
          e: { r: endRow, c: 2 },
        });
      }
    });

    // Merge cells for po_no
    Object.entries(poNoSpanMap).forEach(([key, spanInfo]) => {
      if (spanInfo.count > 1) {
        const startRow = spanInfo.firstIndex + 1;
        const endRow = startRow + spanInfo.count - 1;
        merges.push({
          s: { r: startRow, c: 3 },
          e: { r: endRow, c: 3 },
        });
      }
    });

    ws['!merges'] = merges;

    const colWidths = [
      { wch: 8 },
      { wch: 20 },
      { wch: 18 },
      { wch: 15 },
      { wch: 12 },
      { wch: 20 },
      { wch: 12 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
      { wch: 10 },
      { wch: 15 },
    ];

    ws['!cols'] = colWidths;

    const borderStyle = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    };

    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: '4F81BD' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: borderStyle,
    };

    for (let col = 0; col < headers.length; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!ws[cellRef]) ws[cellRef] = { t: 's', v: headers[col] };
      ws[cellRef].s = headerStyle;
    }

    for (
      let row = 1;
      row < wsData.length - (sortedData.length > 0 ? 1 : 0);
      row++
    ) {
      for (let col = 0; col < headers.length; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        const cellStyle = {
          alignment: {
            horizontal: 'center',
            vertical: 'center',
            wrapText: true, // Enable wrap text for workorder_no and po_no cells with newlines
          },
          border: borderStyle,
        };

        if (ws[cellRef]) {
          ws[cellRef].s = cellStyle;
        } else {
          ws[cellRef] = { t: 's', v: '', s: cellStyle };
        }
      }
    }

    if (sortedData.length > 0) {
      const totalRowIndex = wsData.length - 1;
      for (let col = 0; col < headers.length; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });
        const totalRowStyle = {
          font: { bold: true },
          fill: { fgColor: { rgb: 'E7E6E6' } },
          alignment: {
            horizontal: 'center',
            vertical: 'center',
          },
          border: borderStyle,
        };

        if (ws[cellRef]) {
          ws[cellRef].s = totalRowStyle;
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'WorkOrder Report');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `Work_Order_Report_${sortedData[0]?.customer_name || 'Customer'}_${dayjs().format('DD-MM-YYYY')}.xlsx`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  const filteredColumns = columns.filter(column =>
    checkedList.includes(column.key),
  );

  return (
    <>
      <div>
        <div className="sticyaction">
          <Row className="mb-0" justify="space-between" align="middle">
            <Col>
              <h2 style={{ margin: 0 }}>View Section</h2>
              <div className="bredcrumbwrp">
                <Link to="/die-profilelist" className="back">
                  <Back /> BACK
                </Link>
                <Breadcrumb
                  items={[
                    { title: <Link to="/die-profilelist">Section</Link> },
                    { title: 'View Section' },
                  ]}
                />
              </div>
            </Col>
            <Col>
              {currentUser.permissions.permissions.die.includes(
                'change_die',
              ) && (
                  <Button
                    type="primary"
                    style={{
                      marginBottom: '20px',

                      marginRight: '10px',
                    }}
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/edit-dieprofile/${dieProfile?.id}`)}
                  >
                    Edit Section
                  </Button>
                )}
              {currentUser.permissions.permissions.die.includes(
                'download_profile_pdf_copy',
              ) && (
                  <Tooltip
                    placement="top"
                    title={'Download PDF'}
                    arrow={mergedArrow}
                  >
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      style={{ marginBottom: '20px', float: 'right' }}
                      onClick={downloadPdf}
                    >
                      Download PDF
                    </Button>
                  </Tooltip>
                )}
            </Col>
          </Row>
        </div>
        <div
          className="px-4"
          style={{ position: 'sticky', top: 0, zIndex: 1000, background: '#fff' }}
        >
          <Row gutter={[16, 16]} className="viewform">
            <Col xs={24} sm={18}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Section Number</label>
                  <div>{dieProfile?.die_number}</div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">
                    Customer Refrence Number{' '}
                  </label>
                  <div>{dieProfile?.customer_reference_number || 'N/A'}</div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">
                    Description
                  </label>
                  <div>{dieProfile?.description || 'N/A'}</div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Section Description</label>
                  <div>
                    {[
                      dieProfile?.dimension1 &&
                      `${Number(dieProfile.dimension1).toFixed(2)} MM`,
                      dieProfile?.dimension2 &&
                      `${Number(dieProfile.dimension2).toFixed(2)} MM`,
                      dieProfile?.dimension3 &&
                      `${Number(dieProfile.dimension3).toFixed(2)} MM`,
                      dieProfile?.dimension4 &&
                      `${Number(dieProfile.dimension4).toFixed(2)} MM`,
                    ]
                      .filter(Boolean) // Remove null or falsy values
                      .join(' X ') || 'N/A'}
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Section Group</label>
                  <div>
                    {dieProfile?.die_group !== null
                      ? dieProfile?.die_group?.name
                      : 'None'}
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Section Category</label>
                  <div>
                    {dieProfile?.die_category !== null
                      ? dieProfile?.die_category?.name
                      : 'N/A'}
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Section SubCategory</label>
                  <div>
                    {dieProfile?.die_sub_category !== null &&
                      dieProfile?.die_sub_category.name !== ''
                      ? dieProfile?.die_sub_category?.name
                      : 'N/A'}
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Calculated Kg/Mtr</label>
                  <div>{dieProfile?.wt_kg_p_mt}</div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Weight Range Kg/Mtr</label>
                  <div>
                    {dieProfile?.min_wt_kg_p_mt !== undefined
                      ? Number(dieProfile?.min_wt_kg_p_mt)?.toFixed(2)
                      : '-'}
                    -
                    {dieProfile?.max_wt_kg_p_mt !== undefined
                      ? Number(dieProfile?.max_wt_kg_p_mt)?.toFixed(2)
                      : '-'}
                  </div>
                </Col>

                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Section Type</label>
                  <div>
                    {dieProfile?.die_type !== null
                      ? dieProfile?.die_type
                      : 'N/A'}
                  </div>
                </Col>

                {/* <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">
                    {' '}
                    Total Running Kilometer
                  </label>
                  <div>
                    {dieProfile?.total_running_ton !== null
                      ? dieProfile?.total_running_ton
                      : 'N/A'}
                  </div>
                </Col> */}

                {/* <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Total Running Quantity</label>
                  <div>
                    {dieProfile?.total_running_qty !== null
                      ? dieProfile?.total_running_qty
                      : '0.0'}
                  </div>
                </Col> */}
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label
                   className="customlabel"
                  >
                    Section Detail Diagram
                  </label>
                  {dieProfile?.die_detail_diagram !== null ? (
                    <Button
                      type="secondry"
                      onClick={() =>
                        showModal(
                          dieProfile?.die_detail_diagram,
                          'Die Detail Diagram',
                        )
                      }
                    >
                      View File
                    </Button>
                  ) : (
                    <div>N/A</div>
                  )}
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label
                    className="customlabel"
                  >
                    Customer Approved Die Diagram
                  </label>
                  {dieProfile?.customer_approved_diagram !== null ? (
                    <Button
                      type="secondry"
                      onClick={() =>
                        showModal(
                          dieProfile?.customer_approved_diagram,
                          'Customer Approved Die Diagram',
                        )
                      }
                    >
                      View File
                    </Button>
                  ) : (
                    <div>N/A</div>
                  )}
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label
                    className="customlabel"
                    style={{ display: 'block', marginBottom: '8px' }}
                  >
                    Autocad Drawing
                  </label>

                  {dieProfile?.autocad_drawing !== null ? (
                    <Button
                      type="secondry"
                      onClick={() =>
                        showModal(
                          dieProfile?.autocad_drawing,
                          'Autocad Drawing',
                        )
                      }
                    >
                      Download File
                    </Button>
                  ) : (
                    <div>N/A</div>
                  )}
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label
                    className="customlabel"
                    style={{ display: 'block', marginBottom: '8px' }}
                  >
                    Section Manufacturing
                  </label>
                  {dieProfile?.die_manufacturing !== null ? (
                    <Button
                      type="secondry"
                      onClick={() =>
                        showModal(
                          dieProfile?.die_manufacturing,
                          'Die Manufacturing Diagram',
                        )
                      }
                    >
                      Download File
                    </Button>
                  ) : (
                    <div>N/A</div>
                  )}
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label
                    className="customlabel"
                    style={{ display: 'block', marginBottom: '8px' }}
                  >
                    SOP
                  </label>
                  {dieProfile?.die_sop !== null ? (
                    <Button
                      type="secondry"
                      onClick={() => showModal(dieProfile?.die_sop, 'SOP')}
                    >
                      View File
                    </Button>
                  ) : (
                    <div>N/A</div>
                  )}
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Remarks</label>
                  <div>
                    {dieProfile?.remarks !== null ? dieProfile?.remarks : 'N/A'}
                  </div>
                </Col>
                <Modal
                  title={modalTitle}
                  open={isModalVisible}
                  onCancel={handleCancel}
                  footer={null}
                  width={800}
                >
                  {popupData ? (
                    <img
                      src={popupData}
                      alt=""
                      style={{ width: '100%', height: 'auto' }}
                    />
                  ) : (
                    <p>No diagram available.</p>
                  )}
                </Modal>
              </Row>
            </Col>
            <Col xs={24} sm={6}>
              <label
                className="customlabel"
              >
                Section Diagram:
              </label>
              {dieProfile && dieProfile.die_diagram !== null ? (
                (() => {
                  const extension = getFileExtension(dieProfile.die_diagram);
                  const isImage = [
                    'jpg',
                    'jpeg',
                    'png',
                    'gif',
                    'bmp',
                    'webp',
                  ].includes(extension);
                  const isPdf = extension === 'pdf';

                  if (isImage) {
                    return (
                      <img
                        src={dieProfile.die_diagram}
                        alt="Profile Diagram"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100px',
                          objectFit: 'contain',
                        }}
                      />
                    );
                  } else if (isPdf) {
                    return (
                      <Button
                        type="secondry"
                        onClick={() =>
                          showModal(
                            dieProfile.die_diagram,
                            'Profile Diagram PDF',
                          )
                        }
                      >
                        View PDF
                      </Button>
                    );
                  } else {
                    return (
                      <Button
                        type="secondary"
                        onClick={() =>
                          showModal(dieProfile.die_diagram, 'Profile Diagram')
                        }
                      >
                        View File
                      </Button>
                    );
                  }
                })()
              ) : (
                <div>N/A</div>
              )}
            </Col>
          </Row>
          <Divider />
          {dieProfile?.balloon_drawing_dimensions.length > 0 && (
            <>
              <h3 style={{ marginBottom: '15px' }}>Drawing Dimension Details</h3>
              <Table
                dataSource={dieProfile?.balloon_drawing_dimensions}
                columns={ballonDrawingDimensionColumns}
                pagination={false}

              />
            </>
          )}
          <Divider />
          <div>
            <Card
              title={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>Die Tools List</span>
                  <div>
                    <Space size="small">
                      <Input
                        placeholder="Search"
                        className="searchbox"
                        prefix={<SearchOutlined />}
                        value={search}
                        style={{ width: 200 }}
                        onChange={handleSearch}
                      />
                      <Tooltip placement="top" title="Reset Filter">
                        <Button
                          type="primary"
                          disabled={!search && !orderBy}
                          onClick={resetFilter}
                        >
                          <FilterIcon />
                        </Button>
                      </Tooltip>
                      {currentUser.permissions.permissions.die.includes(
                        'download_profile_tool_excel_copy',
                      ) && (
                          <Tooltip placement="top" title={'Export Excel'}>
                            <Button
                              type="primary"
                              className="iconlink"
                              style={{ width: '10px' }}
                            >
                              <CSVLink
                                filename={'die_tool.csv'}
                                data={csvListData}
                                className="iconlink"
                                headers={csvFilterheaders}
                              >
                                <FileExcelOutlined />
                              </CSVLink>
                            </Button>
                          </Tooltip>
                        )}
                      {currentUser.permissions.permissions.die.includes(
                        'download_profile_tool_pdf_copy',
                      ) && (
                          <Tooltip placement="top" title={'Export PDF'}>
                            <Button
                              type="primary"
                              className="iconlink"
                              onClick={exportDieToolToPDF}
                            >
                              <FilePdfOutlined />
                            </Button>
                          </Tooltip>
                        )}
                      {currentUser.permissions.permissions.die.includes(
                        'add_dietool',
                      ) && (
                          <Link to={`/add-die-tool-profile/${id}`}>
                            <Tooltip placement="top" title={'Add Die Tool'}>
                              <Button type="primary" icon={<PlusOutlined />}>
                                Add Die Tool
                              </Button>
                            </Tooltip>
                          </Link>
                        )}
                      {currentUser.permissions.permissions.die.includes(
                        'delete_dietool',
                      ) && (
                          <Tooltip placement="top" title="Archive">
                            <Link to="/archive-die-tool" className="iconlink">
                              <Button type="primary">
                                <Archiveicon style={{ cursor: 'pointer' }} />
                              </Button>
                            </Link>
                          </Tooltip>
                        )}
                      <Tooltip
                        placement="topLeft"
                        title="Column Filter Options"
                      >
                        <Dropdown
                          overlay={columnMenu}
                          trigger={['click']}
                          placement="bottomRight"
                          open={dropdownVisible} // Change this line
                          onOpenChange={setDropdownVisible}
                        >
                          <Button
                            type="primary"
                            icon={<ColumnList />}
                            onClick={e => e.preventDefault()}
                          />
                        </Dropdown>
                      </Tooltip>
                    </Space>
                  </div>
                </div>
              }
              style={{ marginBottom: '20px' }}
              styles={{
                header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' },
              }}
            >
              <Table
                className="px-0"
                rowKey="id"
                columns={filteredColumns}
                dataSource={toolsList}
                pagination={{
                  current: page, // Update the current page based on state
                  pageSize: pagesize,
                  total: totalRecord,
                  showSizeChanger: true,
                  onChange: handlePageChange, // Update page when changed
                }}
                onChange={handleTableChange}
                scroll={{
                  x: 1500,
                }}
              />
            </Card>
          </div>
        </div>
      </div>
      <Divider />
      <div
        style={{ marginTop: '100px', marginLeft: '20px', marginRight: '20px' }}
      >
        <Card
          title={
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Work Order Report</span>
              <div>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={handlePrint}
                  disabled={!orderData.length}
                  style={{ marginRight: '8px' }}
                >
                  Print
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExcelDownload}
                  disabled={!orderData.length}
                  style={{ marginRight: '8px' }}
                >
                  Excel
                </Button>
                <Button
                  type="primary"
                  onClick={() => fetchWorkOrderData()}
                  loading={orderLoading}
                >
                  Refresh
                </Button>
              </div>
            </div>
          }
          style={{ marginBottom: '20px' }}
          styles={{
            header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' },
          }}
        >
          {orderLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <p style={{ marginTop: '10px' }}>Loading work order data...</p>
            </div>
          ) : orderData.length > 0 ? (
            <Table
              rowKey="id"
              columns={workOrderColumns}
              dataSource={processedData}
              pagination={false}
              scroll={{ y: 'calc(100vh - 400px)' }}
              bordered
              summary={() => {
                const totalOrderPcs = orderData.reduce(
                  (sum, order) => sum + (parseInt(order.order_pcs) || 0),
                  0,
                );
                const totalOrderKgs = orderData.reduce(
                  (sum, order) => sum + (parseFloat(order.order_kgs) || 0),
                  0,
                );
                const totalDispatchedPcs = orderData.reduce(
                  (sum, order) => sum + (parseInt(order.dispatched_pcs) || 0),
                  0,
                );
                const totalDispatchedKgs = orderData.reduce(
                  (sum, order) => sum + (parseFloat(order.dispatched_kgs) || 0),
                  0,
                );
                const totalPackedPcs = orderData.reduce(
                  (sum, order) => sum + (parseInt(order.packed_pcs) || 0),
                  0,
                );
                const totalPackedgs = orderData.reduce(
                  (sum, order) => sum + (parseFloat(order?.packed_kgs) || 0),
                  0,
                );

                const totalWarehousePcs = orderData.reduce(
                  (sum, order) => sum + (parseInt(order?.warehouse_pcs) || 0),
                  0,
                );
                const totalWarehouseKgs = orderData.reduce(
                  (sum, order) => sum + (parseFloat(order?.warehouse_kgs) || 0),
                  0,
                );
                const totalPendingPcs = orderData.reduce(
                  (sum, order) => sum + (parseInt(order?.pending_pcs) || 0),
                  0,
                );
                const totalPendingKgs = orderData.reduce(
                  (sum, order) => sum + (parseFloat(order?.pending_kgs) || ''),
                  0,
                );

                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={8}>
                        <strong>Total</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="center">
                        <strong>{totalOrderPcs}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="center">
                        <strong>{totalOrderKgs.toFixed(3)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="center">
                        <strong>{totalDispatchedPcs}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={9} align="center">
                        <strong>{totalDispatchedKgs.toFixed(3)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={10} align="center">
                        <strong>{totalPackedPcs}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={11} align="center">
                        <strong>{totalPackedgs.toFixed(3)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={12} align="center">
                        <strong>{totalWarehousePcs}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={13} align="center">
                        <strong>{totalWarehouseKgs.toFixed(3)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={14} align="center">
                        <strong>{totalPendingPcs}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={15} align="center">
                        <strong>{totalPendingKgs.toFixed(3)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={16}></Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
              <p>No Data.</p>
            </div>
          )}
        </Card>
      </div>
      <div ref={printRef} style={{ display: 'none' }}>
        {/* This content is for print reference only */}
      </div>

      <style jsx>{`
        .customlabel {
          font-weight: 600;
          color: #555;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
          display: block;
        }

        . {
          font-size: 14px;
          color: #333;
          font-weight: 500;
          padding: 8px 12px;
          background: #f8f9fa;
          border-radius: 4px;
          border-left: 3px solid #007bff;
          min-height: 36px;
          display: flex;
          align-items: center;
        }

        .viewform .ant-col {
          margin-bottom: 16px;
        }

        .sticyaction {
          position: sticky;
          top: 0;
          z-index: 100;
          background: white;
          padding: 16px 24px;
          border-bottom: 1px solid #e8e8e8;
          margin-bottom: 20px;
        }

        .pagenamerow h2 {
          color: #1f2937;
          font-size: 24px;
          font-weight: 600;
        }

        .bredcrumbwrp {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 8px;
        }

        .back {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #6b7280;
          text-decoration: none;
          font-weight: 500;
          padding: 6px 12px;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .back:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .ant-card {
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        .ant-card-head {
          border-bottom: 2px solid #e5e7eb;
        }

        .ant-card-head-title {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }

        .ant-table-thead > tr > th {
          background: #f8fafc;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #e5e7eb;
        }

        .ant-table-tbody > tr > td {
          padding: 12px 16px;
        }

        .ant-table-tbody > tr:hover > td {
          background: rgb(255, 255, 255);
        }

        .ant-btn {
          border-radius: 6px;
          font-weight: 500;
          transition: all 0.2s;
        }

        .ant-btn-primary {
          background: #374151;
          border-color: #374151;
        }

        .ant-btn-primary:hover {
          background: #374151;
          border-color: #374151;
        }

        @media (max-width: 768px) {
          .sticyaction {
            padding: 12px 16px;
          }

          .px-4 {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }

          . {
            font-size: 13px;
            padding: 6px 10px;
          }
        }
      `}</style>
    </>
  );
};

export default ViewDieProfile;
